import { computed, effect, Injectable, signal, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

export interface LoadingState {
  [key: string]: boolean;
}

export interface SuccessState {
  [key: string]: boolean;
}

export interface SnackbarState {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private snackBar = inject(MatSnackBar);

  // Signals for reactive state management
  private loadingState = signal<LoadingState>({});
  private successState = signal<SuccessState>({});
  private lastSnackbar = signal<SnackbarState | null>(null);

  // Public readonly signals (consumers can read but not write)
  readonly loading = this.loadingState.asReadonly();
  readonly success = this.successState.asReadonly();
  readonly snackbar = this.lastSnackbar.asReadonly();

  // Computed signals for common queries
  readonly hasAnyLoading = computed(
    () => Object.keys(this.loadingState()).length > 0
  );

  readonly hasAnySuccess = computed(
    () => Object.keys(this.successState()).length > 0
  );

  private defaultSuccessConfig: MatSnackBarConfig = {
    duration: 4000,
    horizontalPosition: 'center',
    verticalPosition: 'bottom',
    panelClass: ['success-snackbar'],
  };

  private defaultErrorConfig: MatSnackBarConfig = {
    duration: 6000,
    horizontalPosition: 'center',
    verticalPosition: 'bottom',
    panelClass: ['error-snackbar'],
  };

  private defaultWarningConfig: MatSnackBarConfig = {
    duration: 5000,
    horizontalPosition: 'center',
    verticalPosition: 'bottom',
    panelClass: ['warning-snackbar'],
  };

  private defaultInfoConfig: MatSnackBarConfig = {
    duration: 4000,
    horizontalPosition: 'center',
    verticalPosition: 'bottom',
    panelClass: ['info-snackbar'],
  };

  constructor() {
    // Optional: Log notifications in development
    if (!this.isProduction()) {
      effect(() => {
        const snackbar = this.lastSnackbar();
        if (snackbar) {
          console.log(`[Snackbar ${snackbar.type}]:`, snackbar.message);
        }
      });
    }
  }

  /**
   * Show success notification
   */
  showSuccess(
    message: string,
    action: string = '✕',
    config?: MatSnackBarConfig
  ): void {
    const finalConfig = { ...this.defaultSuccessConfig, ...config };
    this.snackBar.open(`${message}`, action, finalConfig);

    this.lastSnackbar.set({
      message,
      type: 'success',
      timestamp: Date.now(),
    });
  }

  /**
   * Show error notification
   */
  showError(
    message: string,
    action: string = '✕',
    config?: MatSnackBarConfig
  ): void {
    const finalConfig = { ...this.defaultErrorConfig, ...config };
    this.snackBar.open(`${message}`, action, finalConfig);

    this.lastSnackbar.set({
      message,
      type: 'error',
      timestamp: Date.now(),
    });
  }

  /**
   * Show warning notification
   */
  showWarning(
    message: string,
    action: string = '✕',
    config?: MatSnackBarConfig
  ): void {
    const finalConfig = { ...this.defaultWarningConfig, ...config };
    this.snackBar.open(`${message}`, action, finalConfig);

    this.lastSnackbar.set({
      message,
      type: 'warning',
      timestamp: Date.now(),
    });
  }

  /**
   * Show info notification
   */
  showInfo(
    message: string,
    action: string = '✕',
    config?: MatSnackBarConfig
  ): void {
    const finalConfig = { ...this.defaultInfoConfig, ...config };
    this.snackBar.open(`ℹ ${message}`, action, finalConfig);

    this.lastSnackbar.set({
      message,
      type: 'info',
      timestamp: Date.now(),
    });
  }

  /**
   * Set loading state for a specific operation
   */
  setLoading(key: string, isLoading: boolean): void {
    this.loadingState.update((state) => {
      const newState = { ...state };
      if (isLoading) {
        newState[key] = true;
      } else {
        delete newState[key];
      }
      return newState;
    });
  }

  /**
   * Check if a specific operation is loading
   */
  isLoading(key: string): boolean {
    return this.loadingState()[key] === true;
  }

  /**
   * Create a computed signal for a specific loading key
   */
  isLoadingSignal(key: string) {
    return computed(() => this.loadingState()[key] === true);
  }

  /**
   * Show success state briefly (for visual feedback like checkmark)
   */
  showSuccessState(key: string, duration: number = 2000): void {
    this.successState.update((state) => ({ ...state, [key]: true }));

    setTimeout(() => {
      this.successState.update((state) => {
        const newState = { ...state };
        delete newState[key];
        return newState;
      });
    }, duration);
  }

  /**
   * Check if a specific operation shows success state
   */
  hasSuccessState(key: string): boolean {
    return this.successState()[key] === true;
  }

  /**
   * Create a computed signal for a specific success key
   */
  hasSuccessStateSignal(key: string) {
    return computed(() => this.successState()[key] === true);
  }

  /**
   * Clear all loading states
   */
  clearAllLoading(): void {
    this.loadingState.set({});
  }

  /**
   * Clear all success states
   */
  clearAllSuccessStates(): void {
    this.successState.set({});
  }

  /**
   * Get all currently loading keys
   */
  getLoadingKeys = computed(() => Object.keys(this.loadingState()));

  /**
   * Get all currently successful keys
   */
  getSuccessKeys = computed(() => Object.keys(this.successState()));

  private isProduction(): boolean {
    // You can update this based on your environment detection
    return false;
  }
}
